// Los dos campos de clave nueva, con checklist en vivo y medidor de fuerza.
//
// Es un componente compartido a propósito: lo usan la pantalla del enlace del
// correo y el "Cambiar clave" de Ajustes. Si cada una definiera sus reglas, el
// médico vería requisitos distintos según por dónde entró.
import React, { useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckIcon from "@mui/icons-material/Check";

import { passwordRules, passwordStrength } from "../../helpers/password-reset-api";
import { fieldLabelSx, inputSx } from "./auth-styles";

const NIVELES = {
  1: "var(--color-danger)",
  2: "var(--color-warning)",
  3: "var(--color-success)",
};

const PasswordFields = ({
  password,
  repeat,
  onPasswordChange,
  onRepeatChange,
  email = "",
  disabled = false,
  autoFocus = false,
}) => {
  const [visible, setVisible] = useState(false);
  const rules = passwordRules(password, email);
  const strength = passwordStrength(password, email);
  const noCoinciden = repeat.length > 0 && password !== repeat;

  const campo = (label, value, onChange, id, extraProps = {}) => (
    <Box>
      <Typography component="label" htmlFor={id} sx={fieldLabelSx}>
        {label}
      </Typography>
      <Box sx={{ position: "relative" }}>
        <Box
          component="input"
          id={id}
          name={id}
          type={visible ? "text" : "password"}
          value={value}
          disabled={disabled}
          autoComplete="new-password"
          onChange={(e) => onChange(e.target.value)}
          sx={{ ...inputSx, pr: "44px" }}
          {...extraProps}
        />
        <IconButton
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Ocultar la clave" : "Mostrar la clave"}
          sx={{
            position: "absolute",
            right: 4,
            top: "50%",
            transform: "translateY(-50%)",
            color: "text.muted",
            // 44pt de área tocable: abajo de eso no se puede apretar en un
            // teléfono sin errarle.
            minWidth: 44,
            minHeight: 44,
          }}
        >
          {visible ? (
            <VisibilityOffOutlinedIcon fontSize="small" />
          ) : (
            <VisibilityOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Stack spacing={1.5}>
      {/* Campo oculto con el correo: sin esto, Chrome y 1Password guardan la
          clave nueva en una entrada huérfana y el médico queda peor que antes. */}
      {email ? (
        <Box component="input" type="text" name="email" value={email} autoComplete="username"
             readOnly hidden aria-hidden="true" sx={{ display: "none" }} onChange={() => {}} />
      ) : null}

      {campo("Clave nueva", password, onPasswordChange, "new-password", {
        autoFocus,
      })}

      {password ? (
        <Box>
          <Stack direction="row" spacing={0.5}>
            {[1, 2, 3].map((n) => (
              <Box
                key={n}
                sx={{
                  height: 3,
                  flex: 1,
                  borderRadius: "2px",
                  backgroundColor:
                    strength.level >= n
                      ? NIVELES[strength.level]
                      : "rgba(var(--fg-rgb),0.10)",
                  transition: "background-color 160ms cubic-bezier(0.22,1,0.36,1)",
                }}
              />
            ))}
          </Stack>
          <Typography
            sx={{ fontSize: 11.5, mt: 0.6, color: NIVELES[strength.level] || "text.muted" }}
          >
            {strength.label}
          </Typography>
        </Box>
      ) : null}

      <Stack spacing={0.3} sx={{ mt: 0.2 }}>
        {rules.map((r) => (
          <Stack key={r.id} direction="row" spacing={0.8} alignItems="center">
            <CheckIcon
              sx={{
                fontSize: 14,
                color: r.ok && password ? "var(--color-success)" : "text.faint",
              }}
            />
            <Typography
              sx={{
                fontSize: 11.5,
                color: r.ok && password ? "var(--color-success)" : "text.muted",
              }}
            >
              {r.label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {campo("Repite la clave nueva", repeat, onRepeatChange, "repeat-password")}
      {noCoinciden ? (
        <Typography sx={{ fontSize: 11.5, color: "error.main", fontWeight: 600 }}>
          Las dos claves no coinciden.
        </Typography>
      ) : null}
    </Stack>
  );
};

export default PasswordFields;
